import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Pagination,
  Paper,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import CommonList from '../common/components/CommonList';
import { ListSelectionMode } from '../common/components/types';
import { useDebtSummary } from './hooks/useDebtSummary';
import type { DebtDirection, DebtSummary } from '../../types/debt';

export default function DebtPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  if (!id) {
    throw new Error('Company ID is required');
  }

  const companyId = parseInt(id);
  const [direction, setDirection] = useState<DebtDirection>('CLIENT_OWES');
  const [showFilters, setShowFilters] = useState(false);

  const {
    data: { summaries, loading, error },
    pagination: { page, count: totalPages, onChange: handlePageChange },
    filters: { includeSettled, onIncludeSettledChange: handleIncludeSettledChange },
  } = useDebtSummary(companyId, direction);

  const goToLedger = (summary: DebtSummary) => {
    navigate(`/companies/${companyId}/debts/contacts/${summary.contactId}?direction=${direction}`);
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 10,
          backgroundColor: 'background.default',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          py: 1,
          mb: 0.5,
        }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/companies/${companyId}/home`)}
          size="small"
        >
          На головну
        </Button>
        <Typography
          variant="button"
          component="h1"
          noWrap
          sx={{
            minWidth: 0,
            pr: { xs: '9px', sm: '10px' },
            color: 'primary.main',
          }}
        >
          Борги
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Tabs
          value={direction}
          onChange={(_event, value) => setDirection(value)}
        >
          <Tab label="Нам винні" value="CLIENT_OWES" />
          <Tab label="Ми винні" value="WE_OWE" />
        </Tabs>

        <Tooltip title="Показати/сховати фільтри">
          <IconButton
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters || includeSettled ? 'primary' : 'default'}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 2, backgroundColor: 'background.default' }} variant="outlined">
          <FormControlLabel
            control={(
              <Checkbox
                checked={includeSettled}
                onChange={(e) => handleIncludeSettledChange(e.target.checked)}
              />
            )}
            label="Показати погашені"
          />
        </Paper>
      </Collapse>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {loading && summaries.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
          {loading && <LinearProgress sx={{ mb: 2 }} />}

          <CommonList<DebtSummary>
            items={summaries}
            toListItem={(item) => ({
              id: item.contactId,
              primary: item.contactName || 'Без імені',
              secondary: `$${item.balance.toFixed(2)}`,
              tertiary: item.lastEntryDate
                ? new Date(item.lastEntryDate).toLocaleDateString('uk-UA')
                : undefined,
            })}
            selectionMode={ListSelectionMode.VIEW}
            showSelection={false}
            onItemClick={goToLedger}
            emptyMessage={direction === 'CLIENT_OWES' ? 'Ніхто нам не винен' : 'Ми нікому не винні'}
          />

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                page={page + 1}
                count={totalPages}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
